import axios from 'axios';

import logger from './logger.service';
import {
  collectStreamOffsetSources,
  getTimeElapsedSinceBroadcastStartMs,
  isOnDemandReplay
} from './sessionPlayback.service';
import { getWatcherCount } from './utils/floatingEmoji.utils';

import analytics from '@/analytics';
import { cventAnalytics } from '@/analytics/cvent';
import {
  BURST_MIN_ABSOLUTE,
  BURST_PERCENT,
  MAX_FLOATERS_PER_EMOJI
} from '@/components/FloatingEmoji/floatingEmoji.constants';
import { getEnv } from '@/constants';
import store from '@/store';
import { exponentialRetry } from '@/utils/utils';
import {
  EMOJI_LOOKUP_MAP,
  FloatingEmojiReaction,
  FloatingEmojiParticle,
  ParticleMode,
  ReactionListener
} from '@/views/event/FloatingEmoji/types';

const BASE_URL = getEnv('VUE_APP_FLOATING_EMOJIS_URL');

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 300;

/**************************************************/
// Optimistic deduplication
// When the user clicks, the caller spawns a local particle and triggers
// sendFloatingEmoji. The socket arrives ~1–2s later carrying a count that
// includes the user's own click. To avoid a double-spawn we register one
// optimistic entry per API send, then subtract that from the socket count.
/**************************************************/
const pendingOptimistic = new Map<string, number>();

// Safety-net TTL: if the socket never arrives (network hiccup), clear the
// pending entry so a future socket message isn't under-counted.
const OPTIMISTIC_TTL_MS = 5000;

const ttlTimers = new Map<string, ReturnType<typeof setTimeout>>();

function registerOptimistic(emojiId: string): void {
  pendingOptimistic.set(emojiId, (pendingOptimistic.get(emojiId) ?? 0) + 1);

  const existing = ttlTimers.get(emojiId);
  if (existing !== undefined) {
    clearTimeout(existing);
  }
  ttlTimers.set(
    emojiId,
    setTimeout(() => {
      pendingOptimistic.delete(emojiId);
      ttlTimers.delete(emojiId);
    }, OPTIMISTIC_TTL_MS)
  );
}

function clearTtl(emojiId: string): void {
  const timer = ttlTimers.get(emojiId);
  if (timer !== undefined) {
    clearTimeout(timer);
    ttlTimers.delete(emojiId);
  }
}

const listeners: Set<ReactionListener> = new Set();

export const onFloatingEmojiReaction = (listener: ReactionListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const emitParticles = (particles: FloatingEmojiParticle[]): void => {
  listeners.forEach(l => l(particles));
};

let burstThresholdOverride: number | null = null;

function getBurstThreshold(): number {
  if (burstThresholdOverride !== null) {
    return burstThresholdOverride;
  }
  return Math.max(BURST_MIN_ABSOLUTE, Math.ceil(getWatcherCount() * BURST_PERCENT));
}

export const spawnSingleReaction = (emoji: FloatingEmojiReaction['emoji']): void => {
  emitParticles([{ emoji, mode: ParticleMode.Float }]);
};

export const handleSocketReactionPayload = (data: {
  broadcast_id: string;
  emojis: Array<{ emoji_id: string; count: number }>;
}): void => {
  // Tab is hidden → reactions arrived while the user was away and are already
  // stale. Discard them rather than bursting all animations on tab focus.
  if (document.hidden || !store.state.event.useFloatingEmoji || store.state.event.hideFloatingEmojiReactions) {
    return;
  }

  const particles: Array<FloatingEmojiParticle> = [];

  data.emojis.forEach(emoji => {
    const { emoji_id, count } = emoji;
    const emojiTexture = EMOJI_LOOKUP_MAP.get(emoji_id);

    const optimistic = pendingOptimistic.get(emoji_id) ?? 0;
    const deducted = Math.min(optimistic, count);

    if (deducted === optimistic) {
      pendingOptimistic.delete(emoji_id);
      clearTtl(emoji_id);
    } else {
      pendingOptimistic.set(emoji_id, optimistic - deducted);
    }
    const effectiveCount = count - deducted;

    if (!emojiTexture || effectiveCount <= 0) {
      return;
    }

    if (effectiveCount >= getBurstThreshold()) {
      particles.push({
        emoji: emojiTexture.emoji,
        mode: ParticleMode.Cannon,
        count: effectiveCount
      });
    } else {
      const floatCount = Math.min(effectiveCount, MAX_FLOATERS_PER_EMOJI);
      for (let j = 0; j < floatCount; j++) {
        particles.push({
          emoji: emojiTexture.emoji,
          mode: ParticleMode.Float
        });
      }
    }
  });

  if (particles.length) {
    emitParticles(particles);
  }
};

const sendRequest = async (token: string, eventId: string, broadcastId: string, emojiId: string): Promise<void> => {
  await axios.post(
    `${BASE_URL}/events/${eventId}/broadcasts/${broadcastId}/floating_emojis`,
    { emoji_id: emojiId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

export const sendFloatingEmoji = async (emoji: FloatingEmojiReaction): Promise<void> => {
  const event = store.getters['event/currentEvent'];
  const broadcast = store.getters['event/currentBroadcast'];

  registerOptimistic(emoji.id);
  analytics.track('floating_emoji_sent', {
    emoji_id: emoji.id,
    emoji: emoji.emoji,
    ...getFloatingEmojiAnalyticsProperties()
  });

  const postEmoji = async (): Promise<void> => {
    const token = store.state.user.accessToken;
    try {
      await sendRequest(token, event.id, broadcast.id, emoji.id);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        await store.dispatch(
          'fetchAccessToken',
          { isUnauthorized: true, error: err.response.data?.error },
          { root: true }
        );
        const retryToken = store.state.user.accessToken;
        await sendRequest(retryToken, event.id, broadcast.id, emoji.id);
        return;
      }
      throw err;
    }
  };

  await exponentialRetry(postEmoji, {
    retries: MAX_RETRIES,
    delayMs: RETRY_DELAY_MS,
    onRetry: (e, attempt) => {
      logger.warn(`Error sending floating emoji, retry attempt ${attempt}:`, e);
    },
    onMaxReached: e => {
      logger.error(`Max retries reached for sending floating emoji:`, e);
    }
  });

  if (!isOnDemandReplay()) {
    cventAnalytics.trackReactionAdded({ rootGetters: store.getters }, emoji.id, collectStreamOffsetSources());
  }
};

export const getFloatingEmojiAnalyticsProperties: () => {
  eventId: string;
  broadcastId: string;
  broadcastStartTime: string;
  timeElapsedSinceBroadcastStart: number;
} = () => {
  const event = store.getters['event/currentEvent'];
  const broadcast = store.getters['event/currentBroadcast'];

  return {
    eventId: event.id,
    broadcastId: broadcast.id,
    broadcastStartTime: broadcast.live_start_time,
    timeElapsedSinceBroadcastStart: getTimeElapsedSinceBroadcastStartMs()
  };
};

export const _testing = {
  getBurstThreshold,
  getWatcherCount,
  setBurstThresholdOverride: (v: number | null): void => {
    burstThresholdOverride = v;
  },
  BURST_MIN_ABSOLUTE,
  BURST_PERCENT
};
